// Order status popover + owner confirm dialog (orders + checkout)
import { getTg } from '../core/telegram.js';
import { closestEl } from '../core/dom.js';

const orderPopover = document.getElementById('order-popover');
const orderPopoverText = document.getElementById('order-popover-text');
let orderPopoverCloseTimer: ReturnType<typeof setTimeout> | null = null;
let orderPopoverHideAnimTimer: ReturnType<typeof setTimeout> | null = null;

function ensureOrderPopoverPortal(): void {
  if (!orderPopover || !document.body) return;
  if (orderPopover.parentElement !== document.body) {
    document.body.appendChild(orderPopover);
  } else if (orderPopover !== document.body.lastElementChild) {
    document.body.appendChild(orderPopover);
  }
}

function getOrderPopoverHeaderReserve(): number {
  var intro = document.querySelector('.orders-page-intro');
  if (intro && typeof intro.getBoundingClientRect === 'function') {
    var introRect = intro.getBoundingClientRect();
    if (introRect.bottom > 0) return Math.ceil(introRect.bottom + 8);
  }
  var safeTop = 0;
  try {
    safeTop = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-top')) || 0;
  } catch (_) {}
  if (!safeTop) {
    try {
      safeTop = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)')) || 0;
    } catch (_) {}
  }
  return Math.max(72, Math.ceil(safeTop + 56));
}

function layoutOrderPopoverCard(cardEl: HTMLElement, anchorEl: Element | null): void {
  var rect =
    anchorEl && typeof anchorEl.getBoundingClientRect === 'function'
      ? anchorEl.getBoundingClientRect()
      : null;
  var cardW = Math.max(180, Math.min(window.innerWidth - 24, cardEl.offsetWidth || 220));
  var rawCenterX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
  var minCenterX = 12 + cardW / 2;
  var maxCenterX = window.innerWidth - 12 - cardW / 2;
  var centerX = Math.max(minCenterX, Math.min(maxCenterX, rawCenterX));
  cardEl.style.left = centerX + 'px';

  var headerReserve = getOrderPopoverHeaderReserve();
  var cardH = Math.max(cardEl.offsetHeight, cardEl.scrollHeight, 48);
  var gap = 10;
  var viewportBottom = window.innerHeight - 12;

  if (rect) {
    var placeAboveTop = rect.top - gap;
    var placeBelowTop = rect.bottom + gap;
    var fitsAbove = placeAboveTop - cardH >= headerReserve;
    var fitsBelow = placeBelowTop + cardH <= viewportBottom;
    var preferBelow = placeAboveTop - cardH < headerReserve;

    if (preferBelow && fitsBelow) {
      cardEl.style.top = placeBelowTop + 'px';
      cardEl.style.setProperty('--popover-base-transform', 'translate(-50%, 0)');
      cardEl.style.setProperty('--popover-transform-origin', 'center top');
      return;
    }
    if (fitsAbove) {
      cardEl.style.top = placeAboveTop + 'px';
      cardEl.style.setProperty('--popover-base-transform', 'translate(-50%, -100%)');
      cardEl.style.setProperty('--popover-transform-origin', 'center bottom');
      return;
    }
    if (fitsBelow) {
      cardEl.style.top = placeBelowTop + 'px';
      cardEl.style.setProperty('--popover-base-transform', 'translate(-50%, 0)');
      cardEl.style.setProperty('--popover-transform-origin', 'center top');
      return;
    }
    cardEl.style.top = Math.max(headerReserve + cardH + gap, Math.min(placeAboveTop, viewportBottom - gap)) + 'px';
    cardEl.style.setProperty('--popover-base-transform', 'translate(-50%, -100%)');
    cardEl.style.setProperty('--popover-transform-origin', 'center bottom');
    return;
  }

  cardEl.style.top = headerReserve + gap + 'px';
  cardEl.style.setProperty('--popover-base-transform', 'translate(-50%, 0)');
  cardEl.style.setProperty('--popover-transform-origin', 'center top');
}

export function confirmOwnerAction(
  title: string,
  message: string,
  okText: string,
  okIsDestructive: boolean
): Promise<boolean> {
  return new Promise(function (resolve) {
    try {
      const tg = getTg();
      const showPopup = tg?.showPopup;
      if (showPopup) {
        showPopup.call(
          tg,
          {
            title: String(title || ''),
            message: String(message || ''),
            buttons: [
              { id: 'cancel', type: 'cancel', text: 'Отмена' },
              { id: 'ok', type: okIsDestructive ? 'destructive' : 'default', text: String(okText || 'Ок') }
            ]
          },
          function (btnId: string) {
            resolve(btnId === 'ok');
          }
        );
        return;
      }
    } catch (_) {}
    try {
      resolve(window.confirm(String(message || 'Подтвердить действие?')));
    } catch (_) {
      resolve(false);
    }
  });
}

export function hideOrderPopover(): void {
  if (!orderPopover) return;
  if (orderPopoverHideAnimTimer) {
    try { clearTimeout(orderPopoverHideAnimTimer); } catch (_) {}
    orderPopoverHideAnimTimer = null;
  }
  orderPopover.classList.remove('order-popover-open');
  orderPopover.classList.add('order-popover-closing');
  orderPopover.setAttribute('aria-hidden', 'true');
  orderPopoverHideAnimTimer = setTimeout(function () {
    if (!orderPopover) return;
    orderPopover.classList.remove('order-popover-closing');
    try {
      const card = document.getElementById('order-popover-card');
      if (card) {
        card.style.left = '';
        card.style.top = '';
        card.style.removeProperty('--popover-base-transform');
        card.style.removeProperty('--popover-transform-origin');
      }
    } catch (_) {}
    orderPopoverHideAnimTimer = null;
  }, 220);
  if (orderPopoverCloseTimer) {
    try { clearTimeout(orderPopoverCloseTimer); } catch (_) {}
    orderPopoverCloseTimer = null;
  }
}

export function showOrderPopover(text: string, anchorEl: Element | null): void {
  if (!orderPopover || !orderPopoverText) return;
  ensureOrderPopoverPortal();
  if (orderPopoverCloseTimer) {
    try { clearTimeout(orderPopoverCloseTimer); } catch (_) {}
    orderPopoverCloseTimer = null;
  }
  if (orderPopoverHideAnimTimer) {
    try { clearTimeout(orderPopoverHideAnimTimer); } catch (_) {}
    orderPopoverHideAnimTimer = null;
  }
  orderPopoverText.textContent = String(text || '');
  orderPopover.classList.remove('order-popover-closing');
  orderPopover.classList.remove('order-popover-open');
  orderPopover.setAttribute('aria-hidden', 'false');
  try {
    const cardEl = document.getElementById('order-popover-card');
    if (cardEl) {
      layoutOrderPopoverCard(cardEl, anchorEl);
    }
  } catch (_) {}
  try {
    requestAnimationFrame(function () {
      if (!orderPopover) return;
      try {
        const cardEl = document.getElementById('order-popover-card');
        if (cardEl) layoutOrderPopoverCard(cardEl, anchorEl);
      } catch (_) {}
      orderPopover.classList.add('order-popover-open');
    });
  } catch (_) {
    orderPopover.classList.add('order-popover-open');
  }
  orderPopoverCloseTimer = setTimeout(hideOrderPopover, 2600);
}

ensureOrderPopoverPortal();

const popoverEl = orderPopover;
if (popoverEl && popoverEl.dataset.popoverInit !== '1') {
  popoverEl.dataset.popoverInit = '1';
  document.addEventListener('pointerdown', function (e: PointerEvent) {
    if (!popoverEl.classList.contains('order-popover-open')) return;
    if (closestEl(e.target, '.order-info-btn')) return;
    if (closestEl(e.target, '#order-popover-card')) return;
    hideOrderPopover();
  }, true);
  window.addEventListener('resize', hideOrderPopover);
  window.addEventListener('scroll', hideOrderPopover, true);
}
